// Shared language contract for the storefront. Intentionally NOT imported from
// the services `@local/locale` package — different repo, different concern. We
// share the CONVENTION (same `["en","de"]` union), not the code (per plan §M3).

export const SUPPORTED_LANGUAGES = ["en", "de"] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]

export const DEFAULT_LANGUAGE: Language = "en"

const SUPPORTED_SET = new Set<string>(SUPPORTED_LANGUAGES)

const isLanguage = (value: string): value is Language =>
  SUPPORTED_SET.has(value)

/**
 * Coerce an arbitrary locale/language input to a supported {@link Language},
 * falling back to `en`. Accepts a bare code (`"de"`) or a full locale tag
 * (`"de-DE"`) — only the primary subtag is considered, lower-cased.
 */
export const languageOf = (input: string | null | undefined): Language => {
  if (!input) return DEFAULT_LANGUAGE
  const primary = input.trim().toLowerCase().split(/[-_]/)[0]
  return isLanguage(primary) ? primary : DEFAULT_LANGUAGE
}

/**
 * DEV-9850: public market-language variants.
 *
 * The market↔language relation is many-to-many (spec: `language_in_market`
 * in the offer DB, every market's set includes "en", default via
 * `markets.default_language`). M5 ships this registry as the hardcoded
 * code-level projection of that relation; DEV-9851 swaps it to data.
 * Registry shape mirrors the schema on purpose.
 */
export interface MarketLanguageVariant {
  market: string
  language: Language
  /** Explicit hreflang token — derived codes are unsafe (see en-xx warts). */
  hreflang: string
  /** Human label for the market/region selector. */
  label: string
}

export const MARKET_LANGUAGE_VARIANTS: readonly MarketLanguageVariant[] = [
  {
    market: "de",
    language: "en",
    hreflang: "en-DE",
    label: "Germany (English)",
  },
]

/** Per-market default language ("is_main"). Markets absent here default to en. */
const MARKET_DEFAULT_LANGUAGE: Record<string, Language> = {
  de: "de",
}

const VARIANT_BY_SLUG = new Map(
  MARKET_LANGUAGE_VARIANTS.map((v) => [`${v.market}-${v.language}`, v])
)

export const defaultLanguageFor = (market: string): Language =>
  MARKET_DEFAULT_LANGUAGE[market] ?? DEFAULT_LANGUAGE

/**
 * Decompose a public market slug. Total: any input yields a market string
 * and a supported Language. A registered compound ("de-en") splits into its
 * base market and variant language; anything else is treated as a base
 * market rendering its default language.
 */
export const parseMarketSlug = (
  slug: string | null | undefined
): { market: string; language: Language } => {
  const normalized = slug?.trim().toLowerCase() ?? ""
  const variant = VARIANT_BY_SLUG.get(normalized)
  if (variant) {
    return { market: variant.market, language: variant.language }
  }
  return { market: normalized, language: defaultLanguageFor(normalized) }
}

/** Compose the public slug: bare market for its default, compound otherwise. */
export const composeMarketSlug = (
  market: string,
  language: Language
): string =>
  language === defaultLanguageFor(market) ? market : `${market}-${language}`

/** Base market of any public slug — the ONLY form the backend may see. */
export const marketBaseOf = (slug: string | null | undefined): string =>
  parseMarketSlug(slug).market

/**
 * Rewrites a backend-built, market-prefixed path so it stays in the visitor's
 * market-language variant. Backend URLs are baked with the base market (it
 * only knows base markets — DEV-9850); on a variant slug like "de-en" the
 * leading "/de/" segment must be rewritten to "/de-en/". No-op for base slugs
 * and for URLs not prefixed with the slug's base market.
 */
export const localizeMarketPath = (
  path: string,
  marketSlug: string
): string => {
  const base = marketBaseOf(marketSlug)
  if (base === marketSlug) return path
  // The base prefix must be a full path segment: followed by "/", end of
  // string, a query string, or a fragment ("/de?ref=x" counts, "/decor" does not).
  const after = path.slice(base.length + 1)
  return path.startsWith(`/${base}`) &&
    (after === "" ||
      after.startsWith("/") ||
      after.startsWith("?") ||
      after.startsWith("#"))
    ? `/${marketSlug}${after}`
    : path
}

/** Registered variant slugs for a market (e.g. "de" → ["de-en"]). */
export const variantSlugsFor = (market: string): string[] =>
  MARKET_LANGUAGE_VARIANTS.filter((v) => v.market === market).map((v) =>
    composeMarketSlug(v.market, v.language)
  )

/**
 * The languages a market serves, default first, then its registered variant
 * languages (deduped). Single-language markets return `[defaultLanguageFor(m)]`.
 * Registry-driven: adding a market to MARKET_LANGUAGE_VARIANTS extends this
 * automatically with no code change here.
 */
export const languagesForMarket = (market: string): Language[] => {
  const base = defaultLanguageFor(market)
  const variants = MARKET_LANGUAGE_VARIANTS.filter(
    (v) => v.market === market
  ).map((v) => v.language)
  return [base, ...variants.filter((l) => l !== base)]
}

/**
 * Pilot market → language resolution. Where the fetched market object is
 * available (RSC pages, post-DEV-9526) prefer `languageOf(market.language)`
 * directly; this slug-based map is the fallback for call sites that only have a
 * market slug. Pilot: the `de` market speaks German; everything else is
 * English — now parse-backed to account for registered market variants.
 */
export const getLanguageForMarket = (
  marketSlug: string | null | undefined
): Language => {
  if (!marketSlug) return DEFAULT_LANGUAGE
  return parseMarketSlug(marketSlug).language
}

/**
 * BCP-47 tag for a language, used to seed `Intl.*` formatters. The pilot maps
 * German to the full regional tag `de-DE` (correct grouping/decimal separators
 * and currency-symbol placement); English stays the bare `en` so `Intl` keeps
 * its neutral English behaviour.
 */
export const bcp47 = (language: Language): string =>
  language === "de" ? "de-DE" : "en"
